/**
 * dealer-map.js
 * Google Maps: markers from coordinates and/or Geocoding from address + city + state.
 *
 * Expects: window.dealerData (array) and window.dealerMapSettings (object)
 * from dealer-map.liquid. Enable Geocoding API on the same Google Cloud key.
 */

(function () {
  'use strict';

  let map;
  let markers = [];
  let infoWindows = [];
  let activeInfoWindow = null;
  let activeListItem = null;

  function escapeHtml(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function parseCoord(v) {
    if (v == null || v === '') return NaN;
    return Number(String(v).trim().replace(',', '.'));
  }

  function hasStoredCoordinates(dealer) {
    if (dealer.lat == null || dealer.lng == null) return false;
    const lat = parseCoord(dealer.lat);
    const lng = parseCoord(dealer.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    if (lat === 0 && lng === 0) return false;
    return true;
  }

  function buildGeocodeAddress(dealer) {
    const s = window.dealerMapSettings || {};
    const parts = [dealer.address, dealer.city, dealer.state, s.geocodeCountry].filter(function (p) {
      return p != null && String(p).trim() !== '';
    });
    return parts.join(', ');
  }

  function geocodeAddress(geocoder, dealer) {
    const address = buildGeocodeAddress(dealer);
    if (!address) return Promise.resolve(null);
    return new Promise(function (resolve) {
      geocoder.geocode({ address: address }, function (results, status) {
        if (status === 'OK' && results[0]) {
          var loc = results[0].geometry.location;
          resolve({ lat: loc.lat(), lng: loc.lng() });
        } else {
          if (status === 'REQUEST_DENIED' || status === 'INVALID_REQUEST') {
            console.error(
              'Dealer Map: Geocoding failed (' +
                status +
                '). Enable the Geocoding API for this key in Google Cloud Console, and ensure billing is active.'
            );
          } else if (status !== 'ZERO_RESULTS') {
            console.warn('Dealer Map geocode:', status, address);
          }
          resolve(null);
        }
      });
    });
  }

  function delay(ms) {
    return new Promise(function (resolve) {
      setTimeout(resolve, ms);
    });
  }

  window.initDealerMap = function () {
    if (typeof google === 'undefined' || !google.maps) {
      console.error('Dealer Map: Google Maps API did not load. Check the API key and Maps JavaScript API.');
      return;
    }

    const settings = window.dealerMapSettings;
    const dealers = window.dealerData;

    if (!dealers || dealers.length === 0) {
      console.warn('Dealer Map: No dealer data found.');
      return;
    }

    var n = dealers.length;
    markers = new Array(n);
    infoWindows = new Array(n);

    var mapEl = document.getElementById('dealer-map');
    if (!mapEl) {
      console.error('Dealer Map: #dealer-map element not found.');
      return;
    }

    map = new google.maps.Map(mapEl, {
      center: { lat: settings.defaultLat, lng: settings.defaultLng },
      zoom: settings.defaultZoom,
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
      styles: mapStyles()
    });

    hookMapResize();

    var geocoder = new google.maps.Geocoder();

    (async function () {
      for (var i = 0; i < n; i++) {
        var dealer = dealers[i];
        var position = null;

        if (hasStoredCoordinates(dealer)) {
          position = { lat: parseCoord(dealer.lat), lng: parseCoord(dealer.lng) };
        } else {
          position = await geocodeAddress(geocoder, dealer);
          await delay(120);
        }

        if (position) {
          createMarkerAt(dealer, i, position);
        }
      }

      var placed = markers.filter(Boolean).length;
      if (placed === 0) {
        console.warn(
          'Dealer Map: No pins were created. Fill address, city, and state for geocoding, add latitude/longitude on the dealer metaobject, and enable the Geocoding API for this key (same project as Maps JavaScript API).'
        );
      }

      bindListItems();
      bindSearch();
      fitMapToMarkers();
      scheduleMapResize();
    })().catch(function (err) {
      console.error('Dealer Map:', err);
    });
  };

  function createMarkerAt(dealer, index, position) {
    const settings = window.dealerMapSettings;
    var pinColor =
      settings.pinColor && String(settings.pinColor).trim() ? String(settings.pinColor).trim() : '#E63946';

    const marker = new google.maps.Marker({
      position: position,
      map: map,
      title: dealer.name,
      label: {
        text: String(index + 1),
        color: '#ffffff',
        fontSize: '11px',
        fontWeight: '600'
      },
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 16,
        fillColor: pinColor,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2
      }
    });

    const infoWindow = new google.maps.InfoWindow({
      content: buildInfoWindowContent(dealer)
    });

    marker.addListener('click', function () {
      openInfoWindow(infoWindow, marker, index);
    });

    markers[index] = marker;
    infoWindows[index] = infoWindow;
  }

  function buildInfoWindowContent(dealer) {
    const phone = dealer.phone
      ? '<a class="dm-iw-phone" href="tel:' +
        escapeHtml(String(dealer.phone).replace(/\s/g, '')) +
        '">' +
        escapeHtml(dealer.phone) +
        '</a>'
      : '';

    const hours = dealer.hours
      ? '<p class="dm-iw-hours">' +
        escapeHtml(dealer.hours).replace(/\n/g, '<br>') +
        '</p>'
      : '';

    const website = dealer.website
      ? '<a class="dm-iw-link" href=' +
        JSON.stringify(dealer.website) +
        ' target="_blank" rel="noopener">Visit website →</a>'
      : '';

    return (
      '<div class="dm-info-window">' +
      '<strong class="dm-iw-name">' +
      escapeHtml(dealer.name) +
      '</strong>' +
      '<p class="dm-iw-address">' +
      escapeHtml(dealer.address) +
      '<br>' +
      escapeHtml(dealer.city) +
      ', ' +
      escapeHtml(dealer.state) +
      '</p>' +
      phone +
      hours +
      website +
      '</div>'
    );
  }

  function openInfoWindow(infoWindow, marker, index) {
    if (activeInfoWindow) activeInfoWindow.close();
    infoWindow.open(map, marker);
    activeInfoWindow = infoWindow;
    setActiveListItem(index);
    map.panTo(marker.getPosition());
  }

  function bindListItems() {
    const listItems = document.querySelectorAll('.dealer-list-item');

    listItems.forEach(function (item) {
      const btn = item.querySelector('.dealer-list-btn');
      const index = parseInt(item.dataset.index, 10);
      if (!btn) return;

      btn.addEventListener('click', function () {
        const marker = markers[index];
        const infoWindow = infoWindows[index];
        if (!marker || !infoWindow) return;

        openInfoWindow(infoWindow, marker, index);
        map.setZoom(10);
      });
    });
  }

  function setActiveListItem(index) {
    if (activeListItem) activeListItem.classList.remove('is-active');
    const item = document.querySelector('.dealer-list-item[data-index="' + index + '"]');
    if (item) {
      item.classList.add('is-active');
      item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      activeListItem = item;
    }
  }

  function bindSearch() {
    const input = document.getElementById('dealer-search');
    const btn = document.getElementById('dealer-search-btn');

    if (!input) return;

    function runSearch() {
      const query = input.value.trim().toLowerCase();
      filterDealers(query);
    }

    if (btn) btn.addEventListener('click', runSearch);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') runSearch();
    });
    input.addEventListener('input', function () {
      if (input.value === '') filterDealers('');
    });
  }

  function filterDealers(query) {
    const listItems = document.querySelectorAll('.dealer-list-item');
    const countEl = document.getElementById('dealer-count');
    let visibleCount = 0;

    listItems.forEach(function (item) {
      const city = item.dataset.city || '';
      const state = item.dataset.state || '';
      const name = item.dataset.name || '';
      const matches = !query || city.includes(query) || state.includes(query) || name.includes(query);

      item.style.display = matches ? '' : 'none';

      const index = parseInt(item.dataset.index, 10);
      if (markers[index]) {
        markers[index].setVisible(matches);
      }

      if (matches) visibleCount++;
    });

    if (countEl) {
      countEl.textContent = visibleCount + (visibleCount === 1 ? ' location' : ' locations');
    }

    if (query) {
      const bounds = new google.maps.LatLngBounds();
      let hasVisible = false;
      markers.forEach(function (marker) {
        if (marker && marker.getVisible()) {
          bounds.extend(marker.getPosition());
          hasVisible = true;
        }
      });
      if (hasVisible) {
        map.fitBounds(bounds, { top: 64, right: 64, bottom: 64, left: 64 });
        google.maps.event.addListenerOnce(map, 'idle', function () {
          if (map.getZoom() > 11) map.setZoom(11);
          triggerMapResize();
        });
      }
    } else {
      const settings = window.dealerMapSettings;
      map.setCenter({ lat: settings.defaultLat, lng: settings.defaultLng });
      map.setZoom(settings.defaultZoom);
    }
  }

  function fitMapToMarkers() {
    const placed = markers.filter(Boolean);
    if (placed.length === 0) return;

    if (placed.length === 1) {
      map.setCenter(placed[0].getPosition());
      map.setZoom(10);
      triggerMapResize();
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    placed.forEach(function (m) {
      bounds.extend(m.getPosition());
    });
    map.fitBounds(bounds, { top: 64, right: 64, bottom: 64, left: 64 });
    google.maps.event.addListenerOnce(map, 'idle', function () {
      if (map.getZoom() > 11) map.setZoom(11);
      triggerMapResize();
    });
  }

  function triggerMapResize() {
    if (!map || typeof google === 'undefined' || !google.maps) return;
    google.maps.event.trigger(map, 'resize');
  }

  var resizeHooked = false;
  function hookMapResize() {
    if (resizeHooked) return;
    resizeHooked = true;
    var resizeTimer;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(triggerMapResize, 150);
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', triggerMapResize);
  }

  /** Mobile: map div often gets layout after async geocode; resize refreshes tiles. */
  function scheduleMapResize() {
    [0, 120, 400].forEach(function (ms) {
      setTimeout(triggerMapResize, ms);
    });
  }

  function mapStyles() {
    return [
      { featureType: 'poi', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
      { featureType: 'water', stylers: [{ color: '#d4e6f1' }] },
      { featureType: 'landscape', stylers: [{ color: '#f5f5f0' }] },
      { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
      { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#ebebeb' }] },
      { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#c9c9c9' }] }
    ];
  }
})();
